import { useQuery } from '@tanstack/react-query';
import { api, type GuildPermission } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseGuildPermissionsReturn {
  permissions: GuildPermission[];
  /** Convenience predicate for gating UI. */
  can: (permission: GuildPermission) => boolean;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Whether the viewer may open the guild management page at all. The single
 * source of truth for BOTH the detail page's "Manage" button and the
 * management page's own gate — the two drifted once (the button required a
 * narrower set than the page accepted, hiding the page from invite-only and
 * ban-only moderators). `ban_members` is included because the Bans tab is the
 * only unban surface.
 */
export function canOpenGuildManagement(can: (permission: GuildPermission) => boolean): boolean {
  return (
    can('manage_guild') ||
    can('manage_roles') ||
    can('manage_channels') ||
    can('manage_invites') ||
    can('ban_members')
  );
}

/**
 * The CURRENT user's resolved permissions in a guild — base role unioned with
 * every custom role they hold.
 *
 * This gates UI only. Every action it hides is independently enforced server-
 * side; hiding a button is a convenience, never the security boundary.
 */
export function useGuildPermissions(guildId: string | undefined): UseGuildPermissionsReturn {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.guilds.permissions(guildId ?? ''),
    queryFn: () => api.getMyGuildPermissions(guildId!),
    enabled: !!guildId,
  });

  const permissions = data?.permissions ?? [];

  return {
    permissions,
    // While loading we deny — showing a control then yanking it away reads as a
    // bug, and the server would reject the call anyway. Callers gating a whole
    // surface on `can` should check `loading`/`error` first so a slow or failed
    // fetch doesn't read as a permission denial.
    can: (permission: GuildPermission) => permissions.includes(permission),
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load permissions' : null,
    refresh: () => {
      refetch();
    },
  };
}
